"use client"

import { LawnCostResult } from "@/lib/cost-calculator"
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DollarSign, TrendingDown, CheckCircle } from "lucide-react"
import dynamic from "next/dynamic"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { ToolDataChartSkeleton } from "@/components/tools/shared"

const CostDataChart = dynamic(
  () => import("@/components/tools/charts/cost-data-chart").then((m) => m.CostDataChart),
  { ssr: false, loading: () => <ToolDataChartSkeleton height={320} /> }
)

interface CostResultsProps {
  result: LawnCostResult
}

interface CategoryItem {
  description: string
  frequency: string
  totalCost: number
}

function CategorySection({
  name,
  subLabel,
  meta,
  items,
  formatCurrency,
}: {
  name: string
  subLabel?: string
  meta: { percentage: number; amount: number }
  items: CategoryItem[]
  formatCurrency: (amount: number) => string
}) {
  return (
    <div className="p-4 bg-linear-to-br from-green-50/60 to-gray-50 rounded-xl border border-green-100">
      <div className="mb-3">
        <div className="flex justify-between items-baseline gap-3 mb-2">
          <h4 className="text-base font-semibold text-green-900">
            {name}
            {subLabel && <span className="ml-2 text-xs font-normal text-gray-500">({subLabel})</span>}
          </h4>
          <p className="text-sm font-semibold text-green-700 tabular-nums">
            {formatCurrency(meta.amount)}
            <span className="text-xs font-normal text-gray-500 ml-2">{meta.percentage.toFixed(1)}%</span>
          </p>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-1.5 rounded-full bg-green-500 transition-[width]"
            style={{ width: `${meta.percentage}%` }}
          />
        </div>
      </div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex justify-between items-center text-sm bg-white p-2 rounded-lg">
            <div>
              <p className="text-gray-700">{item.description}</p>
              <p className="text-xs text-gray-500">{item.frequency}</p>
            </div>
            <p className="font-semibold text-gray-700 tabular-nums">{formatCurrency(item.totalCost)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

export function CostResults({ result }: CostResultsProps) {
  if (!result || !result.grassType || !result.region) {
    return null
  }

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const breakdownByCategory = new Map(
    result.budgetBreakdown.map(item => [item.category, item])
  )
  const categoryMeta = (name: string) => {
    const item = breakdownByCategory.get(name)
    return {
      percentage: item?.percentage ?? 0,
      amount: item?.amount ?? 0,
    }
  }

  return (
    <div className="space-y-6">
      {/* Cost Breakdown */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" aria-hidden="true" />
            Where your budget goes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {result.fertilizationCosts.length > 0 && (
            <CategorySection
              name="Fertilization"
              meta={categoryMeta('Fertilization')}
              items={result.fertilizationCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.maintenanceCosts.length > 0 && (
            <CategorySection
              name="Maintenance"
              meta={categoryMeta('Maintenance')}
              items={result.maintenanceCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.treatmentCosts.length > 0 && (
            <CategorySection
              name="Treatments"
              meta={categoryMeta('Treatments')}
              items={result.treatmentCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.equipmentCosts && result.equipmentCosts.length > 0 && (
            <CategorySection
              name="Equipment"
              subLabel="Amortized"
              meta={categoryMeta('Equipment')}
              items={result.equipmentCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.waterCosts && (
            <CategorySection
              name="Water"
              meta={categoryMeta('Water')}
              items={[result.waterCosts]}
              formatCurrency={formatCurrency}
            />
          )}
        </CardContent>
      </div>

      {/* Cost Comparison */}
      {(result.diyTotal || result.professionalTotal) && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-green-600" aria-hidden="true" />
              DIY vs professional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {result.diyTotal && (
                <div className="text-center p-5 bg-linear-to-br from-green-50 to-gray-50 rounded-xl border border-green-200">
                  <p className="text-sm text-gray-600 mb-2">DIY</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(result.diyTotal)}</p>
                  <p className="text-xs text-gray-500 mt-2">Materials only</p>
                </div>
              )}

              <div className="text-center p-5 bg-linear-to-br from-green-100 to-green-50 rounded-xl border-2 border-green-400">
                <p className="text-sm text-green-700 font-medium mb-2">Your pick</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(result.annualTotal)}</p>
                <p className="text-xs text-green-600 mt-2 capitalize">
                  {result.careApproach === 'diy' ? 'DIY' : result.careApproach} approach
                </p>
              </div>

              {result.professionalTotal && (
                <div className="text-center p-5 bg-linear-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Professional</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(result.professionalTotal)}</p>
                  <p className="text-xs text-gray-500 mt-2">Full service</p>
                </div>
              )}
            </div>

          </CardContent>
        </div>
      )}

      {/* Cost Saving Tips */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader className="bg-green-50">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <TrendingDown className="w-5 h-5" aria-hidden="true" />
            Ways to spend less
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <ul className="space-y-2">
            {result.costSavingTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-3 text-sm p-2 bg-green-50/50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </div>

      {/* Product Recommendations */}
      <ProductRecommendations
        categories={["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools", "Watering & Irrigation"]}
        title="Budget-friendly products"
        trackingContext="cost-calculator"
        icon={<DollarSign className="w-5 h-5 text-green-600" />}
        keywords={['fertilizer', 'lawn food', result.grassType, result.careApproach].filter(Boolean)}
        compact
      />

      {/* The data behind your budget */}
      <CostDataChart result={result} />

    </div>
  )
}
